import { useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { SectionHead } from "@/components/SectionHead";
import { Seg } from "@/components/Seg";
import { Toggle } from "@/components/Toggle";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/hooks/useAuth";
import { useSettings } from "@/hooks/useSettings";
import { clearDayCache } from "@/lib/api";
import { DATA_BASE_URL } from "@/lib/env";
import { exportProgress } from "@/lib/progressExport";
import { importProgress, resetProgress } from "@/lib/study";
import { deleteAccount, signOut, SIGN_IN_URL, type SyncStatus } from "@/lib/sync";

const STATUS_LABEL: Record<SyncStatus, string> = {
  "signed-out": "Not syncing",
  idle: "Up to date",
  syncing: "Syncing…",
  error: "Sync failed",
  "needs-decision": "Waiting for your choice",
};

export function Settings() {
  const { settings, set } = useSettings();
  const { theme, setTheme } = useTheme();
  const { user, status, error } = useAuth();
  const fileInput = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function onImportFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ""; // let the same file be picked again after a failure
    if (!file) return;
    if (!confirm("Replace all progress on this device with the contents of this file?")) return;

    try {
      const restored = importProgress(JSON.parse(await file.text()));
      setNotice(`Restored ${restored} words.`);
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "Could not read that file.");
    }
  }

  async function confirmDeleteAccount() {
    const ok = confirm(
      "Delete your account and everything synced under it? Progress on this device stays. This cannot be undone."
    );
    if (!ok) return;
    try {
      await deleteAccount();
      setNotice("Account deleted. This device keeps its progress.");
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "Could not delete the account.");
    }
  }

  function confirmReset() {
    const scope = user
      ? "Reset all progress on every signed-in device — labels, review schedule and streak?"
      : "Reset all progress — labels, review schedule and streak?";
    if (confirm(`${scope} This cannot be undone.`)) resetProgress();
  }

  return (
    <div className="page">
      <SectionHead
        kicker="Preferences"
        title={
          <>
            Tune your <span className="italic">copy</span>.
          </>
        }
      />

      <section className="container" style={{ maxWidth: 800 }}>
        <SettingsGroup title="Appearance" num="01">
          <SettingsRow label="Theme" hint="Light is warm bone paper. Dark is night ink.">
            <Seg
              value={theme}
              onChange={setTheme}
              options={[
                ["light", "Light"],
                ["dark", "Dark"],
              ]}
            />
          </SettingsRow>
          <SettingsRow label="Reading size" hint="Affects body text on word entries and examples.">
            <Seg
              value={settings.readingSize}
              onChange={(v) => set("readingSize", v)}
              options={[
                ["S", "S"],
                ["M", "M"],
                ["L", "L"],
                ["XL", "XL"],
              ]}
            />
          </SettingsRow>
          <SettingsRow label="Show IPA" hint="Phonetic transcription next to each word.">
            <Toggle value={settings.showIPA} onChange={(v) => set("showIPA", v)} />
          </SettingsRow>
        </SettingsGroup>

        <SettingsGroup title="Audio" num="02">
          <SettingsRow
            label="Pronunciation source"
            hint="Youdao streams real UK & US recordings — two buttons per word. Browser uses the device's own voice, works offline."
          >
            <Seg
              value={settings.audioSource}
              onChange={(v) => set("audioSource", v)}
              options={[
                ["youdao", "Youdao"],
                ["browser", "Browser"],
                ["off", "Off"],
              ]}
            />
          </SettingsRow>
          {settings.audioSource === "browser" && (
            <SettingsRow label="Browser voice" hint="Accent handed to the Web Speech API.">
              <Seg
                value={settings.audio}
                onChange={(v) => set("audio", v)}
                options={[
                  ["en-US", "US"],
                  ["en-GB", "UK"],
                ]}
              />
            </SettingsRow>
          )}
          <SettingsRow label="Auto-play on flip" hint="Speak the front of each flashcard when shown.">
            <Toggle value={settings.autoPlayOnFlip} onChange={(v) => set("autoPlayOnFlip", v)} />
          </SettingsRow>
        </SettingsGroup>

        <SettingsGroup title="Learning" num="03">
          <SettingsRow label="Daily goal" hint="Number of new words per day.">
            <Seg
              value={String(settings.dailyGoal)}
              onChange={(v) => set("dailyGoal", Number(v))}
              options={[
                ["20", "20"],
                ["30", "30"],
                ["40", "40"],
                ["60", "60"],
              ]}
            />
          </SettingsRow>
          <SettingsRow label="Questions per round" hint="How many questions a Quiz / Typing / Cloze round draws.">
            <Seg
              value={String(settings.roundSize)}
              onChange={(v) => set("roundSize", Number(v))}
              options={[
                ["10", "10"],
                ["20", "20"],
                ["30", "30"],
                ["40", "40"],
              ]}
            />
          </SettingsRow>
          <SettingsRow label="Shuffle flashcards" hint="Randomize order each session.">
            <Toggle
              value={settings.shuffleFlashcards}
              onChange={(v) => set("shuffleFlashcards", v)}
            />
          </SettingsRow>
          <SettingsRow
            label="Auto-advance"
            hint="In Quiz / Typing / Cloze, jump to the next question on its own. Off lets you read the example before moving on."
          >
            <Toggle value={settings.autoNext} onChange={(v) => set("autoNext", v)} />
          </SettingsRow>
        </SettingsGroup>

        <SettingsGroup title="Account" num="04">
          <SettingsRow
            label={user ? "Signed in" : "Sign in"}
            hint={
              user
                ? "Progress follows you to every device signed in to this account."
                : "Sign in to carry your schedule and streak between phone and laptop. Without it, progress stays on this device only."
            }
          >
            {user ? (
              <button className="btn btn-sm" onClick={() => void signOut()} type="button">
                Sign out
              </button>
            ) : (
              <a className="btn btn-sm" href={SIGN_IN_URL}>
                Sign in with Google
              </a>
            )}
          </SettingsRow>
          {user && (
            <SettingsRow label="Account" hint={STATUS_LABEL[status]}>
              <span className="t-mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>
                {user.email}
              </span>
            </SettingsRow>
          )}
          {status === "error" && error && (
            <SettingsRow label="Last sync error" hint="Your answers are safe on this device and will be sent again.">
              <span className="t-mono" style={{ fontSize: 11, color: "var(--accent-red)" }}>
                {error}
              </span>
            </SettingsRow>
          )}
          {user && (
            <SettingsRow label="Delete account" hint="Removes the account and all synced progress. This device keeps its copy.">
              <button
                className="btn btn-sm"
                onClick={() => void confirmDeleteAccount()}
                style={{ borderColor: "var(--accent-red)", color: "var(--accent-red)" }}
                type="button"
              >
                Delete account
              </button>
            </SettingsRow>
          )}
        </SettingsGroup>

        <SettingsGroup title="Data" num="05">
          <SettingsRow label="Sync source" hint="Daily JSON · served from R2.">
            <span className="t-mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>
              {DATA_BASE_URL}
            </span>
          </SettingsRow>
          <SettingsRow label="Cache" hint="In-memory cache of fetched days.">
            <button className="btn btn-sm" onClick={clearDayCache} type="button">Clear cache</button>
          </SettingsRow>
          <SettingsRow label="Export progress" hint="Download a JSON of your mastery state.">
            <button className="btn btn-sm" onClick={() => exportProgress()} type="button">↓ Export</button>
          </SettingsRow>
          <SettingsRow
            label="Import progress"
            hint={
              notice ??
              "Restore an exported file, replacing what's here. When signed in, the restored state wins."
            }
          >
            <>
              <input
                accept="application/json,.json"
                onChange={(e) => void onImportFile(e)}
                ref={fileInput}
                style={{ display: "none" }}
                type="file"
              />
              <button className="btn btn-sm" onClick={() => fileInput.current?.click()} type="button">
                ↑ Import
              </button>
            </>
          </SettingsRow>
          <SettingsRow label="Reset progress" hint="Erase all mastery marks across all days.">
            <button
              className="btn btn-sm"
              onClick={confirmReset}
              style={{ borderColor: "var(--accent-red)", color: "var(--accent-red)" }}
              type="button"
            >
              Reset
            </button>
          </SettingsRow>
        </SettingsGroup>

        <SettingsGroup title="About" num="06">
          <SettingsRow label="Version" hint="Pre-alpha build · subject to change.">
            <span className="t-mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>v0.1.0</span>
          </SettingsRow>
          <SettingsRow label="Corpus" hint="ETS TOEIC 30-day vocabulary set · 1,200 entries.">
            <span className="t-mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>
              ets-toeic-30d
            </span>
          </SettingsRow>
        </SettingsGroup>
      </section>
    </div>
  );
}

function SettingsGroup({ title, num, children }: { title: string; num: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: "var(--s-7)" }}>
      <div className="row-b" style={{ gap: "var(--s-3)", marginBottom: "var(--s-3)" }}>
        <span className="t-mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>§ {num}</span>
        <h2 className="t-display" style={{ fontSize: "var(--t-lg)", margin: 0 }}>{title}</h2>
      </div>
      <div className="settings-list">{children}</div>
    </div>
  );
}

function SettingsRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="settings-row">
      <div className="stack">
        <span className="settings-row__label">{label}</span>
        {hint && <span className="settings-row__hint">{hint}</span>}
      </div>
      <div>{children}</div>
    </div>
  );
}
